import React, { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LoginType, OauthServiceType, useAccountExistenceQuery } from '@websky/graphql';
import { useAuthenticate } from '@features/auth';
import { captureErrorSentry } from '../../utils';
import { prevSocialAuthKeyFactory } from '../../SocialAuth/components/utils';
import { sessionStorageGet } from '../../cache';
import { getParsedParams, parseErrorHandler } from '../utils';
import PromoLoader from '../../PromoLoader';
import { FirstLoginAgreementModal } from '../../LoginPage';

import { SocialAuthParams } from '../types';

interface Props {
	socialAuthCallbackUrl: string;
}

const SocialAuthCallback: FC<Props> = ({ socialAuthCallbackUrl }) => {
	const location = useLocation();

	const [authenticate, { loading }] = useAuthenticate();
	const { refetch: checkAccountExistence } = useAccountExistenceQuery({ skip: true });

	const [checking, setChecking] = useState(false);
	const [parsedParams, setParsedParams] = useState<SocialAuthParams | null>(null);
	const [showFirstLoginAgreement, setShowFirstLoginAgreement] = useState(false);

	const auth = async (params: Object, loginType: LoginType) => {
		try {
			await authenticate({
				variables: {
					params: {
						loginType,
						secureCode: JSON.stringify(params)
					}
				}
			});
		} finally {
			const prevKey = prevSocialAuthKeyFactory(OauthServiceType[loginType]);
			const prevUrl: string = sessionStorageGet(prevKey);

			if (prevUrl) {
				window.location.href = prevUrl;
			} else {
				parseErrorHandler('Social auth callback url has not been found in the session storage');
			}
		}
	};

	const handleContinueAuth = async () => {
		if (!parsedParams) {
			return;
		}

		setShowFirstLoginAgreement(false);

		await auth(parsedParams.params, parsedParams.loginType);
	};

	useEffect(() => {
		let alive = true;

		const run = async () => {
			try {
				const parsed = getParsedParams(location.pathname, socialAuthCallbackUrl);

				if (!parsed) {
					parseErrorHandler('Social auth parse url params error');

					return;
				}

				setParsedParams(parsed);
				setChecking(true);

				const { data } = await checkAccountExistence({
					params: {
						loginType: parsed.loginType,
						login: JSON.stringify(parsed.params)
					}
				});

				if (data?.AccountExistence?.result) {
					await auth(parsed.params, parsed.loginType);

					return;
				}

				if (alive) {
					setShowFirstLoginAgreement(true);
				}
			} catch (e) {
				captureErrorSentry('Social auth account existence error');
			} finally {
				if (alive) {
					setChecking(false);
				}
			}
		};

		run();

		return () => {
			alive = false;
		};
	}, []);

	if (loading || checking) {
		return <PromoLoader />;
	}

	return showFirstLoginAgreement ? (
		<FirstLoginAgreementModal
			modalProps={{
				isOpen: showFirstLoginAgreement
			}}
			firstLoginAgreementProps={{
				onConfirm: handleContinueAuth
			}}
		/>
	) : null;
};

export default SocialAuthCallback;
